import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';

// Create the context
const LangflowContext = createContext();

// Custom hook to use the Langflow context
export const useLangflow = () => {
  const context = useContext(LangflowContext);
  if (!context) {
    throw new Error('useLangflow must be used within a LangflowProvider');
  }
  return context;
};

// Langflow Provider component
export const LangflowProvider = ({
  children,
  baseUrl = 'https://langwatch.frnstaging.org',
  apiKey = 'sk-OMkVjSr49NmF5RmSKP7DORPTO9MXYbR7FPqszn35R5o',
  flowId = 'a4826f78-7f9c-48a9-884a-8e9c05e64d1d',
  userId,
  sessionId
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [currentUserId, setCurrentUserId] = useState(userId);
  const [currentSessionId, setCurrentSessionId] = useState(sessionId || `session-${crypto.randomUUID()}`);

  useEffect(() => {
    if (sessionId) {
      setCurrentSessionId(sessionId);
    }
  }, [sessionId]);

  useEffect(() => {
    if (userId) {
      setCurrentUserId(userId);
    }
  }, [userId]);

  // Clear error state
  const clearError = useCallback(() => {
    setError(null);
  }, []);

  // Add a message to the conversation
  const addMessage = useCallback(message => {
    setMessages(prev => [
      ...prev,
      {
        id: Date.now() + Math.random(),
        timestamp: new Date().toISOString(),
        ...message
      }
    ]);
  }, []);

  // Clear all messages
  const clearMessages = useCallback(() => {
    setMessages([]);
  }, []);

  // Send message to Langflow server
  const sendMessage = useCallback(
    async (messageText, options = {}) => {
      if (!flowId) {
        throw new Error('Flow ID is required to send messages');
      }

      setIsLoading(true);
      setError(null);

      // Add user message immediately
      const userMessage = {
        type: 'user',
        content: messageText,
        sender: 'user'
      };
      addMessage(userMessage);

      try {
        const headers = {
          'Content-Type': 'application/json'
        };

        // Add API key if provided
        if (apiKey) {
          headers['x-api-key'] = `${apiKey}`;
        }

        const requestBody = {
          input_value: messageText,
          output_type: 'chat',
          input_type: 'chat',
          session_id: `${currentUserId}_${currentSessionId}`,
          tweaks: options.tweaks || {},
          ...options.additionalParams
        };

        const response = await fetch(`${baseUrl}/api/v1/run/${flowId}`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const data = await response.json();

        // Extract the assistant's response from Langflow response
        let assistantContent = '';
        if (data.outputs && data.outputs.length > 0) {
          // Try to find the chat output
          const chatOutput = data.outputs.find(output => output.outputs && output.outputs.length > 0);

          if (chatOutput && chatOutput.outputs[0]) {
            const output = chatOutput.outputs[0];
            assistantContent =
              output.results?.message?.text || output.message?.text || output.text || JSON.stringify(output);
          }
        } else if (data.result) {
          assistantContent = data.result;
        } else {
          assistantContent = 'No response received from the server.';
        }

        // Add assistant message
        const assistantMessage = {
          type: 'assistant',
          content: assistantContent,
          sender: 'assistant',
          rawResponse: data
        };
        addMessage(assistantMessage);

        return { userMessage, assistantMessage, rawResponse: data };
      } catch (err) {
        const errorMessage = err.message || 'Failed to send message';
        setError(errorMessage);

        // Add error message to chat
        const errorChatMessage = {
          type: 'error',
          content: `Error: ${errorMessage}`,
          sender: 'system'
        };
        addMessage(errorChatMessage);

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, apiKey, flowId, addMessage]
  );

  // Stream message to Langflow server (for streaming responses)
  const streamMessage = useCallback(
    async (messageText, onChunk, options = {}) => {
      if (!flowId) {
        throw new Error('Flow ID is required to stream messages');
      }

      setIsLoading(true);
      setError(null);

      // Add user message immediately
      const userMessage = {
        type: 'user',
        content: messageText,
        sender: 'user'
      };
      addMessage(userMessage);

      try {
        const headers = {
          'Content-Type': 'application/json',
          accept: 'application/json'
        };

        if (apiKey) {
          headers['x-api-key'] = apiKey;
        }

        const requestBody = {
          input_value: messageText,
          input_type: 'chat',
          output_type: 'chat',
          session_id: `${currentUserId}_${currentSessionId}`,
          tweaks: options.tweaks || {},
          ...options.additionalParams
        };

        // Note: Using stream=true query parameter as per documentation
        const response = await fetch(`${baseUrl}/api/v1/run/${flowId}?stream=true`, {
          method: 'POST',
          headers,
          body: JSON.stringify(requestBody)
        });

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        // Create assistant message placeholder
        const assistantMessageId = Date.now() + Math.random();
        const assistantMessage = {
          id: assistantMessageId,
          type: 'assistant',
          content: '',
          sender: 'assistant',
          streaming: true,
          timestamp: new Date().toISOString()
        };

        setMessages(prev => [...prev, assistantMessage]);
        let assistantContent = '';
        let partialChunk = '';
        const decoder = new TextDecoder();

        try {
          // Use getReader() for better Safari compatibility
          const reader = response.body.getReader();

          while (true) {
            const { done, value } = await reader.read();

            const txt = partialChunk + decoder.decode(value);
            partialChunk = '';
            const events = txt.split('\n');
            if (!txt.endsWith('\n')) {
              partialChunk = events.pop();
            }
            events
              .filter(e => e !== '')
              .map(e => {
                const d = JSON.parse(e);
                if (d.event === 'token') {
                  assistantContent += d.data.chunk;
                } else if (d.event === 'add_message') {
                  if (d.data.sender !== 'Machine') return;
                  assistantContent = d.data.text;
                } else if (d.event === 'end') {
                }
              });
            setMessages(prev =>
              prev.map(msg => {
                return msg.id === assistantMessageId ? { ...msg, content: assistantContent } : msg;
              })
            );
            if (done) break;
          }
        } catch (streamError) {
          console.error('Streaming error:', streamError, response);
          throw streamError;
        }

        // Ensure we have some content in the assistant message and mark streaming as complete
        if (!assistantContent.trim()) {
          assistantContent = 'No response received from the server.';
        }

        // Final update to mark streaming as complete
        setMessages(prev =>
          prev.map(msg =>
            msg.id === assistantMessageId ? { ...msg, content: assistantContent, streaming: false } : msg
          )
        );

        return { userMessage, assistantMessage: { ...assistantMessage, content: assistantContent, streaming: false } };
      } catch (err) {
        const errorMessage = err.message || 'Failed to stream message';
        setError(errorMessage);

        const errorChatMessage = {
          type: 'error',
          content: `Error: ${errorMessage}`,
          sender: 'system'
        };
        addMessage(errorChatMessage);

        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [baseUrl, apiKey, flowId, addMessage, currentSessionId]
  );

  // Test connection to Langflow server
  const testConnection = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const headers = {};
      if (apiKey) {
        headers['Authorization'] = `Bearer ${apiKey}`;
      }

      const response = await fetch(`${baseUrl}/api/v1/flows`, {
        method: 'GET',
        headers
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      return { success: true, data };
    } catch (err) {
      const errorMessage = err.message || 'Failed to connect to Langflow server';
      setError(errorMessage);
      return { success: false, error: errorMessage };
    } finally {
      setIsLoading(false);
    }
  }, [baseUrl, apiKey]);

  const value = {
    // State
    isLoading,
    error,
    messages,

    // Configuration
    baseUrl,
    apiKey,
    flowId,
    currentSessionId,

    // Actions
    sendMessage,
    streamMessage,
    addMessage,
    clearMessages,
    clearError,
    setCurrentUserId,
    testConnection
  };

  return <LangflowContext.Provider value={value}>{children}</LangflowContext.Provider>;
};

export default LangflowProvider;
